import React, { useEffect, useState } from "react";
import { Image as ImageIcon, Loader2, X } from "lucide-react";
import { api } from "@/lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const src = (p) => `${BACKEND}/api/files/${p}`;
const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString("pt-BR") : "");
const clean = (s) => (s || "").replace(/\[\/?b\]/g, "");

const Thumb = ({ path, label, onOpen, testid }) => (
  <button type="button" onClick={() => onOpen(path)} className="relative aspect-square rounded-xl overflow-hidden bg-slate-100 border border-slate-200" data-testid={testid}>
    <img src={src(path)} alt={label || ""} loading="lazy" className="w-full h-full object-cover" />
    {label && <span className="absolute top-1 left-1 text-[9px] font-black uppercase bg-black/60 text-white px-1.5 py-0.5 rounded">{label}</span>}
  </button>
);

export default function SchoolFotos() {
  const [services, setServices] = useState(null);
  const [items, setItems] = useState([]);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    Promise.all([api.get("/school/service-photos"), api.get("/school/fotos")])
      .then(([a, b]) => { setServices(a.data || []); setItems(b.data || []); })
      .catch(() => setServices([]));
  }, []);

  const byItem = items.reduce((acc, p) => {
    const k = `${p.budget_id}-${p.item_id}`;
    (acc[k] = acc[k] || { ...p, before: null, after: null })[p.kind === "antes" ? "before" : "after"] = p.path;
    return acc;
  }, {});

  return (
    <div className="space-y-6" data-testid="school-fotos-page">
      <div>
        <h1 className="font-heading text-2xl font-black text-[#1e3a8a]">Fotos de serviços</h1>
        <p className="text-sm text-slate-500">Fotos dos serviços realizados em sua escola pela equipe AMC</p>
      </div>
      {services === null ? (
        <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
      ) : services.length === 0 && !items.length ? (
        <div className="bg-white rounded-2xl border border-slate-100 card-shadow p-12 text-center" data-testid="school-fotos-empty">
          <ImageIcon className="w-10 h-10 mx-auto text-slate-300 mb-3" />
          <p className="text-sm text-slate-500">Ainda não há fotos de serviços da sua escola.</p>
        </div>
      ) : (
        <>
          {services.length > 0 && (
            <section className="space-y-2">
              <h2 className="text-base font-bold text-slate-800">Mural de serviços ({services.length})</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {services.map((s) => (
                  <figure key={s.id} className="bg-white rounded-xl border border-slate-100 card-shadow overflow-hidden" data-testid={`school-service-photo-${s.id}`}>
                    <Thumb path={s.storage_path} onOpen={setPreview} />
                    <figcaption className="p-2">
                      <p className="text-[12px] text-slate-800 leading-snug line-clamp-3">{s.caption}</p>
                      <p className="text-[10px] text-slate-400 mt-0.5">{fmt(s.created_at)}</p>
                    </figcaption>
                  </figure>
                ))}
              </div>
            </section>
          )}
          {Object.keys(byItem).length > 0 && (
            <section className="space-y-2">
              <h2 className="text-base font-bold text-slate-800">Antes e depois</h2>
              <div className="space-y-2">
                {Object.entries(byItem).map(([k, it]) => (
                  <div key={k} className="bg-white rounded-xl border border-slate-100 card-shadow p-3" data-testid={`school-item-photos-${k}`}>
                    <p className="text-[12px] text-slate-700 leading-snug"><strong className="text-[#1e3a8a]">Orç. Nº {it.budget_number}</strong> · {clean(it.item_description)}</p>
                    <div className="grid grid-cols-2 gap-2 mt-2 max-w-sm">
                      {it.before ? <Thumb path={it.before} label="Antes" onOpen={setPreview} /> : <div />}
                      {it.after ? <Thumb path={it.after} label="Depois" onOpen={setPreview} /> : <div />}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}
        </>
      )}
      {preview && (
        <div onClick={() => setPreview(null)} className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4" data-testid="school-fotos-preview">
          <button type="button" className="absolute top-4 right-4 w-10 h-10 rounded-full bg-black/60 text-white flex items-center justify-center" aria-label="Fechar"><X className="w-5 h-5" /></button>
          <img src={src(preview)} alt="" className="max-h-[90vh] max-w-full rounded-lg" />
        </div>
      )}
    </div>
  );
}
